import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { showToast } from "@int-traderjoes/ui-components-library";
import { validateStorageDetails, validateEntryCollection } from "@/utils/validationHelpers";
import { getMandatoryFields } from "@/utils/helper";
import { PackProductPermissions } from "@/utils/json/FieldPermissions/PackProductPermissions";

interface UsePackProductValidationReturn {
  validatePackProductForm: () => boolean;
  validatePackProductFormSilent: () => boolean;
  packProductFormErrors: string[];
  setPackProductFormErrors: React.Dispatch<React.SetStateAction<string[]>>;
  clearValidationErrors: () => void;
  hasFieldError: (fieldName: string) => boolean;
  clearFieldError: (fieldName: string) => void;
  setFieldError: (fieldName: string) => void;
  isFieldConditionallyMandatory: (fieldName: string) => boolean;
}

export const usePackProductValidation = (): UsePackProductValidationReturn => {
  const { languageText } = useLanguageContext();
  const { packProductForm } = useFormContext();

  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;

  // Helper function to check if a field is conditionally mandatory
  const isFieldConditionallyMandatory = useCallback((fieldName: string): boolean => {
    // Add conditional mandatory logic here if needed
    // For now, all fields use default mandatory setting from permissions
    return true;
  }, []);

  // Memoize mandatory fields for storage details section
  const mandatoryStorageFields = useMemo(() => {
    return ['primaryShelfLifeCodingFromDateOf', 'period', 'formatOfShelfLifeCoding'];
    // Note: bestByDateImprintRequired is not mandatory per datasheet
  }, []);

  // Memoize mandatory fields for pack coding entries
  const mandatoryPackCodingFields = useMemo(() => {
    return ['primaryShelfLife', 'primaryNumber', 'secondaryShelfLife', 'secondaryNumber','primaryOuterCase', 'secondaryOuterCase', 'minimumShelfLifeInfo', 'days', 'defineEntireLotCodeInformation'];
    // Note: primaryOuterCase, secondaryOuterCase, comments are not mandatory per updated requirements
  }, []);

  // Memoize mandatory fields for shelf life coding statement entries
  const mandatoryShelfLifeCodingFields = useMemo(() => {
    return ['statement', 'useOnPack'];
  }, []);

  // State for errors
  const [packProductFormErrors, setPackProductFormErrors] = useState<string[]>([]);

  // Silent validation function (no toast messages)
  const validatePackProductFormSilent = useCallback((): boolean => {
    if (!packProductForm) {
      return false;
    }

    let isFormValid = true;
    const newErrors: string[] = [];

    // Validate Storage Details Section
    const storageValidation = validateStorageDetails(packProductForm, mandatoryStorageFields);
    newErrors.push(...storageValidation.errors);
    if (!storageValidation.isValid) {
      isFormValid = false;
    }

    // Validate Pack Coding Entries
    const packCodingValidation = validateEntryCollection(
      packProductForm.packCodingEntries || [],
      mandatoryPackCodingFields,
      'packCoding',
      'packCodingEntries'
    );
    newErrors.push(...packCodingValidation.errors);
    if (!packCodingValidation.isValid) {
      isFormValid = false;
    }

    // Validate Shelf Life Coding Statement Entries
    const statementValidation = validateEntryCollection(
      packProductForm.shelfLifeCodingStatementEntries || [],
      mandatoryShelfLifeCodingFields,
      'shelfLifeCoding',
      'shelfLifeCodingStatementEntries'
    );
    newErrors.push(...statementValidation.errors);
    if (!statementValidation.isValid) {
      isFormValid = false;
    }

    // Update error state
    setPackProductFormErrors(newErrors);
    
    return isFormValid;
  }, [packProductForm, mandatoryStorageFields, mandatoryPackCodingFields, mandatoryShelfLifeCodingFields]);

  const validatePackProductForm = useCallback((): boolean => {
    if (!packProductForm) {
      showToast({
        type: "error",
        message: errorTexts?.FORM_DATA_NOT_AVAILABLE_ERROR || "Form data is not available"
      });
      return false;
    }

    // Use silent validation first
    const isFormValid = validatePackProductFormSilent();
    
    if (!isFormValid) {
      showToast({
        type: "error",
        message: errorTexts?.FILL_REQUIRED_FIELDS_BEFORE_SAVING || "Please fill in all required fields before saving."
      });
    }
    
    return isFormValid;
  }, [packProductForm, validatePackProductFormSilent, errorTexts]);

  const clearValidationErrors = useCallback(() => {
    setPackProductFormErrors([]);
  }, []);

  const hasFieldError = useCallback((fieldName: string): boolean => {
    // Check for exact match first (used by PackCodingForm and StatementForm with full error keys)
    if (packProductFormErrors.includes(fieldName)) {
      return true;
    }
    
    // Check for pack coding field errors by field name only (packCoding_${entryIndex}_${field})
    const packCodingError = packProductFormErrors.find(error => 
      error.startsWith('packCoding_') && error.endsWith(`_${fieldName}`)
    );
    if (packCodingError) {
      return true;
    }
    
    // Check for statement field errors by field name only (shelfLifeCoding_${entryIndex}_${field})
    const statementError = packProductFormErrors.find(error => 
      error.startsWith('shelfLifeCoding_') && error.endsWith(`_${fieldName}`)
    );
    if (statementError) {
      return true;
    }
    
    return false;
  }, [packProductFormErrors]);

  const clearFieldError = useCallback((fieldName: string) => {
    setPackProductFormErrors(prev => prev.filter(field => field !== fieldName));
  }, []);

  const setFieldError = useCallback((fieldName: string) => {
    setPackProductFormErrors(prev => {
      if (!prev.includes(fieldName)) {
        return [...prev, fieldName];
      }
      return prev;
    });
  }, []);

  return {
    validatePackProductForm,
    validatePackProductFormSilent,
    packProductFormErrors,
    setPackProductFormErrors,
    clearValidationErrors,
    hasFieldError,
    clearFieldError,
    setFieldError,
    isFieldConditionallyMandatory
  };
};